import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Sparkles, Upload, FileText, Download, RefreshCw, File, X, Send, Share, Settings, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

export function ContentEnhancer() {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [enhancedContent, setEnhancedContent] = useState('');
  const [enhancementType, setEnhancementType] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const enhancementTypes = [
    'Simplify Language',
    'Add Examples',
    'Include Visual Aids',
    'Add Interactive Elements',
    'Improve Structure',
    'Add Assessment Questions',
    'Make More Engaging',
    'Add Real-world Applications'
  ];

  const audiences = [
    'Grade 5',
    'Grade 6',
    'Grade 7',
    'Grade 8'
  ];

  const supportedFileTypes = [
    '.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt',
    '.ppt', '.pptx', '.odp',
    '.xls', '.xlsx', '.ods',
    '.jpg', '.jpeg', '.png', '.gif', '.bmp',
    '.mp4', '.avi', '.mov', '.wmv',
    '.mp3', '.wav', '.m4a',
    '.html', '.htm', '.xml'
  ];

  const handleFileUpload = (file: File) => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!supportedFileTypes.includes(fileExtension)) {
      toast.error(`File type ${fileExtension} is not supported. Please upload a valid file.`);
      return;
    }

    setUploadedFile(file);
    
    const sampleContent = generateSampleContentByFileType(fileExtension, file.name);
    setFileContent(sampleContent);
    setOriginalContent(sampleContent);
    toast.success(`File "${file.name}" uploaded successfully!`);
  };

  const generateSampleContentByFileType = (extension: string, fileName: string) => {
    const sampleContents: { [key: string]: string } = {
      '.pdf': `# Mathematics: Introduction to Fractions
      
This document covers the fundamental concepts of fractions, including:

1. Understanding fractions as parts of a whole
2. Identifying numerators and denominators  
3. Comparing fractions
4. Adding and subtracting fractions
5. Real-world applications of fractions

Key concepts:
- A fraction represents a part of a whole
- The numerator tells us how many parts we have
- The denominator tells us how many equal parts the whole is divided into
- Equivalent fractions represent the same value`,

      '.doc': `Subject: Science - The Water Cycle
      
Lesson Objectives:
Students will understand the process of evaporation, condensation, and precipitation.

Content:
The water cycle is a continuous process that circulates water throughout Earth's atmosphere, land, and oceans. It consists of several key stages:

1. Evaporation: Water from oceans, lakes, and rivers turns into water vapor
2. Condensation: Water vapor cools and forms clouds
3. Precipitation: Water falls back to Earth as rain, snow, or hail
4. Collection: Water collects in bodies of water and the cycle repeats`,

      '.ppt': `Presentation: Ancient Civilizations
      
Slide 1: Introduction to Ancient Civilizations
- What makes a civilization?
- Key characteristics: writing, cities, government, religion

Slide 2: Mesopotamia
- Located between Tigris and Euphrates rivers
- First cities and writing system (cuneiform)
- Code of Hammurabi

Slide 3: Ancient Egypt
- Nile River civilization
- Hieroglyphic writing
- Pyramids and pharaohs`
    };

    return sampleContents[extension] || `Content extracted from ${fileName}

This is sample content that would normally be extracted from your uploaded file. The actual implementation would use appropriate libraries to read and extract text from various file formats.

File type: ${extension}
File name: ${fileName}

[Content would be displayed here based on the actual file contents]`;
  };

  const handlePostContent = () => {
    if (!enhancedContent.trim()) {
      toast.error('Please enhance content first before posting');
      return;
    }

    setIsPosting(true);
    
    setTimeout(() => {
      setIsPosting(false);
      toast.success('Enhanced content posted to student portal successfully!');
    }, 1500);
  };

  const handleEnhance = () => {
    const contentToEnhance = originalContent || fileContent;
    if (!contentToEnhance.trim()) {
      toast.error('Please enter content or upload a file to enhance');
      return;
    }

    if (!enhancementType || !targetAudience) {
      toast.error('Please select enhancement type and target audience');
      return;
    }

    setIsEnhancing(true);

    setTimeout(() => {
      const enhanced = generateEnhancedContent(contentToEnhance, enhancementType, targetAudience);
      setEnhancedContent(enhanced);
      setIsEnhancing(false);
      toast.success('Content enhanced successfully!');
    }, 2000);
  };

  const generateEnhancedContent = (content: string, type: string, audience: string) => {
    const enhancements: Record<string, string> = {
      'Simplify Language': `**Enhanced Content for ${audience}**

# Understanding Fractions (Simplified)

Fractions are like pieces of a pizza! 🍕

## What is a Fraction?
A fraction shows us parts of a whole thing. Think of it like this:
- If you have a pizza cut into 4 equal pieces
- And you eat 1 piece
- You ate 1/4 (one-fourth) of the pizza!

## The Two Parts of a Fraction:
1. **Top Number (Numerator)**: How many pieces you have
2. **Bottom Number (Denominator)**: How many pieces the whole thing is divided into

## Easy Examples:
- 1/2 = Half of something (like half a cookie 🍪)
- 1/4 = One piece out of four pieces
- 3/4 = Three pieces out of four pieces

## Fun Activity:
Try this at home! Cut an apple into 4 pieces. If you eat 2 pieces, what fraction did you eat? (Answer: 2/4 or 1/2!)

Remember: Fractions are everywhere - in cooking, sharing, and even in time!`,

      'Add Examples': `**Enhanced Content with Examples for ${audience}**

${content}

## Additional Examples:

### Real-World Fraction Examples:
1. **Cooking**: A recipe calls for 3/4 cup of flour
2. **Sports**: A basketball player made 7 out of 10 free throws (7/10)
3. **Time**: 15 minutes is 1/4 of an hour
4. **Money**: A quarter is 1/4 of a dollar

### Practice Problems:
1. If you eat 2 slices of a pizza that was cut into 8 slices, what fraction did you eat?
2. A class has 24 students. If 18 students passed the test, what fraction passed?
3. You read 50 pages of a 200-page book. What fraction have you completed?

### Visual Examples:
- Draw circles and divide them into equal parts
- Use pie charts to show different fractions
- Create fraction strips using paper`,

      'Include Visual Aids': `**Enhanced Content with Visual Learning for ${audience}**

${content}

## Visual Learning Tools:

### 📊 Fraction Charts and Diagrams:
- Use pie charts to show parts of a whole
- Create fraction bars for comparison
- Draw number lines with fraction markers

### 🎨 Hands-On Activities:
- Fraction pizza cutouts
- Measuring cups for cooking fractions
- Colored blocks or tiles for visualization

### 📱 Interactive Elements:
- Online fraction games and simulators
- Virtual manipulatives for practice
- Fraction calculator tools

### 🖼️ Visual Memory Aids:
- Fraction vocabulary posters
- Step-by-step process charts
- Before and after comparison images`
    };

    return enhancements[type] || `**Enhanced Content for ${audience}**

${content}

---
**Enhancement Applied:** ${type}
**Target Audience:** ${audience}

This content has been enhanced using AI to be more suitable for ${audience}. The enhancement focuses on ${type.toLowerCase()} to improve understanding and engagement.`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(enhancedContent);
    toast.success('Enhanced content copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([enhancedContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'enhanced-content.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Enhanced content downloaded!');
  };

  const handleClear = () => {
    setOriginalContent('');
    setEnhancedContent('');
    setEnhancementType('');
    setTargetAudience('');
    setUploadedFile(null);
    setFileContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileContent('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Content Enhancer</h1>
          <p className="text-muted-foreground mt-1">
            Upload files or add content to enhance with AI-powered improvements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            AI Powered
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <File className="h-3 w-3" />
            Multi-Format
          </Badge>
        </div>
      </div>

      {/* Main Content - Single Page */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Side - Input & Settings */}
        <div className="space-y-6">
          {/* Combined Input Box */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Content Input
              </CardTitle>
              <CardDescription>
                Type your content or upload a file to enhance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="relative">
                  <Textarea
                    value={originalContent}
                    onChange={(e) => setOriginalContent(e.target.value)}
                    placeholder="Paste your lesson content, notes, or educational material here..."
                    className="min-h-[200px] pr-12"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute bottom-2 right-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="h-5 w-5" />
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={supportedFileTypes.join(',')}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                  />
                </div>
                
                {uploadedFile && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <File className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700 flex-1">
                      {uploadedFile.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFile}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  Supports PDF, Word, PowerPoint, Excel, Images, Audio, Video, and more
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Enhancement Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Enhancement Settings
              </CardTitle>
              <CardDescription>
                Configure how you want your content to be enhanced
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Enhancement Type</Label>
                <Select value={enhancementType} onValueChange={setEnhancementType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose how to enhance your content" />
                  </SelectTrigger>
                  <SelectContent>
                    {enhancementTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Select value={targetAudience} onValueChange={setTargetAudience}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your target audience" />
                  </SelectTrigger>
                  <SelectContent>
                    {audiences.map((audience) => (
                      <SelectItem key={audience} value={audience}>
                        {audience}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleEnhance} 
                  className="flex-1"
                  disabled={isEnhancing || (!originalContent.trim() && !fileContent.trim())}
                >
                  {isEnhancing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enhancing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Enhance Content
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Enhanced Content */}
        <div>
          {enhancedContent ? (
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Enhanced Content
                </CardTitle>
                <CardDescription>
                  AI-enhanced version of your content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="max-h-[400px] overflow-y-auto p-4 bg-muted rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm">{enhancedContent}</pre>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={handleCopy} variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                  <Button onClick={handleDownload} variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button 
                    onClick={handlePostContent}
                    disabled={isPosting}
                    className="col-span-1"
                  >
                    {isPosting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Post to Students
                      </>
                    )}
                  </Button>
                  <Button variant="outline">
                    <Share className="h-4 w-4 mr-2" />
                    Share Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full">
              <CardContent className="flex flex-col items-center justify-center py-24">
                <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No Enhanced Content Yet</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  Add content, configure your enhancement settings, and click "Enhance Content" to see AI-powered improvements.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}